import { i as darken, n as alpha, r as createTheme } from "../mui-DmfGpoX6.js";
import { deepmerge } from "@mui/utils";

//#region src/theme/muiExtensions.ts
const chipSoftVariant = "soft";
const chipXSmallSize = "xSmall";
const chipCustomVariantOptions = [chipSoftVariant];
const chipVariantOptions = [
	"filled",
	"outlined",
	...chipCustomVariantOptions
];
const chipCustomSizeOptions = [chipXSmallSize];
const chipSizeOptions = [
	"medium",
	"small",
	...chipCustomSizeOptions
];
const chipColorOptions = [
	"default",
	"primary",
	"secondary",
	"error",
	"info",
	"success",
	"warning"
];
const chipSoftColorOptions = [
	"success",
	"error",
	"warning",
	"info",
	"default",
	"primary"
];

//#endregion
//#region src/theme/theme.ts
const Colors = {
	jotpsych_main: "#36229e",
	jotpsych_dark: "#1f0862",
	brand_white: "#ffffff",
	brand_black: "#000000",
	primary: "#36229e",
	primary900: "#2a2348",
	primary800: "#291d69",
	primary700: "#36229e",
	primary600: "#4d32d7",
	primary500: "#644ce0",
	primary400: "#836eed",
	primary300: "#ada1f3",
	primary200: "#e6e1ff",
	primary100: "#f5f4fc",
	grey900: "#030115",
	grey800: "#2a2533",
	grey700: "#4d4757",
	grey600: "#857f93",
	grey500: "#a39eae",
	grey400: "#bcbac9",
	grey300: "#d5d3e0",
	grey200: "#ebeaf1",
	grey100: "#f5f4f8",
	grey50: "#faf9fc",
	sucess: "",
	success: "#4caf50",
	warning: "#ff9800",
	info: "#2196f3",
	danger: "#eb4a26",
	dangerLight: "#faf6f5",
	body_bg: "",
	border: "",
	inverse: "",
	link: "#2196F3",
	linkHover: "#1976D2",
	eggplant900: "#2A2349",
	eggplant800: "#291D69",
	eggplant700: "#36229E",
	eggplant600: "#4D32D7",
	eggplant500: "#644CE0",
	eggplant400: "#836EED",
	eggplant300: "#AEA1F3",
	eggplant200: "#E6E1FF",
	eggplant100: "#F5F4FB",
	orange100: "#FFC79E",
	yellow200: "#FFDDB0",
	eggplantNeutral900: "#040116",
	eggplantNeutral800: "#2A2533",
	eggplantNeutral700: "#4D4758",
	eggplantNeutral600: "#857F92",
	eggplantNeutral500: "#A39DAF",
	eggplantNeutral400: "#BCBACA",
	eggplantNeutral300: "#D5D3E0",
	eggplantNeutral200: "#EBEAF0",
	eggplantNeutral100: "#F5F4F8",
	eggplantNeutral50: "#FAF9FC"
};
const ChatColors = {
	aiPrimary: Colors.primary600,
	aiSecondary: Colors.primary400,
	aiGradientStart: Colors.primary400,
	aiGradientEnd: Colors.primary600,
	aiDark: Colors.primary700,
	aiLight: Colors.primary300,
	userBubble: Colors.primary600,
	userBubbleHover: Colors.primary700,
	chatBackground: Colors.grey100,
	chatBorder: Colors.grey200,
	messageBubbleBorder: Colors.grey200,
	messageBubbleBackground: Colors.brand_white,
	assistantText: Colors.grey800,
	userText: Colors.brand_white,
	timestampText: Colors.grey600,
	timestampTextLight: Colors.grey500,
	thinkingDot: Colors.primary500,
	streamingCursor: Colors.primary500,
	statusShadow: "rgba(100, 76, 224, 0.4)"
};
const createAIGradient = (angle = 135) => `linear-gradient(${angle}deg, ${ChatColors.aiGradientStart} 0%, ${ChatColors.aiGradientEnd} 100%)`;
const jotpsychThemeOptions = {
	breakpoints: { values: {
		xs: 0,
		sm: 600,
		md: 900,
		lg: 1200,
		xl: 1536,
		xxl: 1920
	} },
	layout: { navHeight: {
		xs: 52,
		sm: 64
	} },
	typography: {
		fontFamilyMono: "ui-monospace, \"SF Mono\", Menlo, Consolas, monospace",
		dataMd: {
			fontSize: 13.5,
			lineHeight: 1.5,
			fontWeight: 400
		},
		dataSm: {
			fontSize: 12,
			lineHeight: 1.4,
			fontWeight: 400
		},
		dataXs: {
			fontSize: 10.5,
			lineHeight: 1.4,
			fontWeight: 400
		}
	},
	palette: {
		primary: {
			main: Colors.jotpsych_main,
			dark: Colors.jotpsych_dark
		},
		secondary: {
			main: "#E55A75",
			dark: "#B7485E"
		},
		grey: {
			50: Colors.grey50,
			100: Colors.grey100,
			200: Colors.grey200,
			300: Colors.grey300,
			400: Colors.grey400,
			500: Colors.grey500,
			600: Colors.grey600,
			700: Colors.grey700,
			800: Colors.grey800,
			900: Colors.grey900
		},
		error: { main: Colors.danger },
		success: { main: Colors.success },
		warning: { main: Colors.warning },
		info: { main: Colors.info },
		customColorMain: {
			100: Colors.primary100,
			200: Colors.primary200,
			300: Colors.primary300,
			400: Colors.primary400,
			500: Colors.primary500,
			600: Colors.primary600,
			700: Colors.primary700,
			800: Colors.primary800,
			900: Colors.primary900
		},
		backgroundColors: {
			primary: Colors.brand_white,
			secondary: Colors.primary100,
			tertiary: Colors.grey100,
			brand: Colors.primary700,
			warning: Colors.dangerLight,
			warningDark: Colors.danger
		},
		borderColors: {
			primary: Colors.primary200,
			lightprimary: Colors.grey200,
			darkprimary: Colors.grey300,
			brand: Colors.primary700,
			warning: Colors.danger
		},
		textColors: {
			lightHeader: Colors.primary900,
			darkHeader: Colors.grey900,
			accent: Colors.primary800,
			brand: Colors.primary700,
			brandContrast: Colors.brand_white,
			lightText: Colors.grey600,
			primaryText: Colors.grey700,
			darkText: Colors.grey900,
			warning: Colors.danger,
			disabled: Colors.grey500
		},
		linkColors: {
			link: Colors.link,
			linkHover: Colors.linkHover
		},
		newDesignColors: {
			eggplant900: Colors.eggplant900,
			eggplant800: Colors.eggplant800,
			eggplant700: Colors.eggplant700,
			eggplant600: Colors.eggplant600,
			eggplant500: Colors.eggplant500,
			eggplant400: Colors.eggplant400,
			eggplant300: Colors.eggplant300,
			eggplant200: Colors.eggplant200,
			eggplant100: Colors.eggplant100,
			orange100: Colors.orange100,
			yellow200: Colors.yellow200,
			eggplantNeutral900: Colors.eggplantNeutral900,
			eggplantNeutral800: Colors.eggplantNeutral800,
			eggplantNeutral700: Colors.eggplantNeutral700,
			eggplantNeutral600: Colors.eggplantNeutral600,
			eggplantNeutral500: Colors.eggplantNeutral500,
			eggplantNeutral400: Colors.eggplantNeutral400,
			eggplantNeutral300: Colors.eggplantNeutral300,
			eggplantNeutral200: Colors.eggplantNeutral200,
			eggplantNeutral100: Colors.eggplantNeutral100,
			eggplantNeutral50: Colors.eggplantNeutral50
		},
		chat: {
			colors: ChatColors,
			gradients: { ai: createAIGradient(135) }
		}
	},
	components: {
		MuiChip: {
			variants: [
				{
					props: {
						variant: "soft",
						color: "success"
					},
					style: ({ theme }) => ({
						backgroundColor: alpha(theme.palette.success.main, .15),
						color: theme.palette.success.dark,
						border: "none"
					})
				},
				{
					props: {
						variant: "soft",
						color: "error"
					},
					style: ({ theme }) => ({
						backgroundColor: alpha(theme.palette.error.main, .15),
						color: theme.palette.error.dark,
						border: "none"
					})
				},
				{
					props: {
						variant: "soft",
						color: "warning"
					},
					style: ({ theme }) => ({
						backgroundColor: alpha(theme.palette.warning.main, .15),
						color: darken(theme.palette.warning.dark, .2),
						border: "none"
					})
				},
				{
					props: {
						variant: "soft",
						color: "info"
					},
					style: ({ theme }) => ({
						backgroundColor: alpha(theme.palette.info.main, .15),
						color: theme.palette.info.dark,
						border: "none"
					})
				},
				{
					props: {
						variant: "soft",
						color: "default"
					},
					style: ({ theme }) => ({
						backgroundColor: alpha(theme.palette.grey[500], .15),
						color: theme.palette.text.secondary,
						border: "none"
					})
				},
				{
					props: {
						variant: "soft",
						color: "primary"
					},
					style: ({ theme }) => ({
						backgroundColor: alpha(theme.palette.primary.main, .15),
						color: theme.palette.primary.dark,
						border: "none"
					})
				}
			],
			styleOverrides: { root: ({ ownerState }) => ({ ...ownerState.size === "xSmall" && {
				height: 20,
				fontSize: "0.75rem",
				"& .MuiChip-label": {
					paddingLeft: 6,
					paddingRight: 6
				},
				"& .MuiChip-icon": {
					fontSize: 14,
					marginLeft: 4,
					marginRight: -2
				},
				"& .MuiChip-deleteIcon": {
					fontSize: 12,
					marginRight: 4,
					marginLeft: -2
				},
				"& .MuiChip-avatar": {
					width: 14,
					height: 14,
					fontSize: "0.5rem",
					marginLeft: 4,
					marginRight: -2
				}
			} }) }
		},
		MuiButton: { defaultProps: { disableRipple: true } },
		MuiTypography: {
			defaultProps: { variantMapping: {
				dataMd: "p",
				dataSm: "span",
				dataXs: "span"
			} },
			styleOverrides: {
				h4: {
					fontSize: "28px",
					fontWeight: 700,
					lineHeight: "33.6px"
				},
				h5: {
					fontSize: "22px",
					fontWeight: 700,
					lineHeight: "26.4px"
				},
				h6: {
					fontSize: "17px",
					fontWeight: 700,
					lineHeight: "25.5px"
				},
				subtitle1: {
					fontSize: "15px",
					fontWeight: 400,
					lineHeight: "22.5px"
				},
				body1: {
					fontSize: "17px",
					fontWeight: 400,
					lineHeight: "25.5px"
				},
				caption: {
					fontSize: "0.8rem",
					fontWeight: 300
				}
			}
		}
	}
};
const createJotPsychTheme = (extendTheme) => {
	const baseTheme = createTheme(jotpsychThemeOptions);
	if (!extendTheme) return baseTheme;
	return createTheme(deepmerge(jotpsychThemeOptions, typeof extendTheme === "function" ? extendTheme(baseTheme) : extendTheme));
};
const jotpsychTheme = createJotPsychTheme();

//#endregion
export { ChatColors, Colors, chipColorOptions, chipCustomSizeOptions, chipCustomVariantOptions, chipSizeOptions, chipSoftColorOptions, chipSoftVariant, chipVariantOptions, chipXSmallSize, createAIGradient, createJotPsychTheme, jotpsychTheme, jotpsychThemeOptions };
//# sourceMappingURL=index.js.map